'use client'

import React, { useEffect, useState } from 'react'
import { FileText } from 'lucide-react'
import { pdfUrlToThumbnailDataUrl } from '@/shared/lib/pdf-thumbnail'

export interface PdfThumbnailProps {
  /** Same-origin API path to the PDF (e.g. /api/photos/123/download, /api/base-photos/456/download). */
  downloadApiPath: string
  /** Optional className for the wrapper or img. */
  className?: string
  alt?: string
  /** Inline style for the thumbnail (e.g. height/width to match timeline cards). */
  style?: React.CSSProperties
  /** Fallback content when thumbnail is loading or failed (e.g. FileText + "PDF" label). */
  children?: React.ReactNode
}

/**
 * Renders a thumbnail of the first page of a PDF. Uses same-origin download API to avoid CORS.
 * Shows children (fallback) while loading or on error.
 */
export function PdfThumbnail({
  downloadApiPath,
  className,
  alt = 'PDF preview',
  style,
  children,
}: PdfThumbnailProps) {
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!downloadApiPath) return
    setDataUrl(null)
    setFailed(false)
    pdfUrlToThumbnailDataUrl(downloadApiPath)
      .then(setDataUrl)
      .catch(() => setFailed(true))
  }, [downloadApiPath])

  if (dataUrl) {
    return (
      <>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={dataUrl}
          alt={alt}
          className={className}
          style={style}
        />
      </>
    )
  }

  return <>{children}</>
}
